import {
  Box,
  FormControl,
  FormControlProps,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { InputLabelProps } from '@mui/material/InputLabel/InputLabel';
import { SelectProps } from '@mui/material/Select/Select';
import { ReactNode, useCallback } from 'react';
import { v4 as uuidV4 } from 'uuid';

import { Enum } from '~/data/enums';
import { BillableHoursTypeConfig } from '~/data/rewarding/types';
import { theme } from '~/theme';

const getLinkedInputLabelAndSelectProps = (fieldProps: {
  required?: boolean;
  label: ReactNode;
  selectId: string;
}): {
  inputLabelProps: Partial<InputLabelProps>;
  selectProps: Partial<SelectProps>;
} => {
  const { selectId, label, ...common } = fieldProps;

  const selectLabelId = `${selectId}Label`;

  return {
    inputLabelProps: {
      ...common,
      children: label,
      id: selectLabelId,
    },
    selectProps: {
      ...common,
      label,
      id: selectId,
      labelId: selectLabelId,
    },
  };
};

export type BillableHoursTypeConfigSelectorProps = {
  selectedHoursTypeConfig?: BillableHoursTypeConfig;
  onSelectedHoursTypeConfigChange: (
    typeConfig: BillableHoursTypeConfig | undefined,
  ) => Promise<BillableHoursTypeConfig | undefined>;
  typeEnums: Enum[];
  options: BillableHoursTypeConfig[];
  currency: string;
  getNonPresentHoursTypeConfig: (hoursType: string) => BillableHoursTypeConfig;
};

type PropsType = SelectProps &
  BillableHoursTypeConfigSelectorProps & {
    FormControlProps?: FormControlProps;
    includeEmptyOption?: boolean;
    helperText?: string;
    onHoursTypeChange: (hoursType: string | undefined) => void;
  };

export const BillableHoursTypeConfigSelector = (props: PropsType) => {
  const {
    value,
    options,
    typeEnums,
    getNonPresentHoursTypeConfig,
    selectedHoursTypeConfig,
    onSelectedHoursTypeConfigChange,
    onHoursTypeChange,
    currency,
    FormControlProps,
    includeEmptyOption,
    helperText,
    children,
    onChange,
    ...selectProps
  } = props;

  const { label, required, id } = selectProps;
  const linkedComponentProps = getLinkedInputLabelAndSelectProps({
    label,
    selectId: id || uuidV4(),
    required,
  });

  const handleSelect: SelectProps['onChange'] = useCallback(
    (event) => {
      const selectedTypeConfig = options.find(
        (option) => option.fEmplSubUId === event.target.value,
      );

      // NOTE: This component has dual action
      // 1) Selects a config that will be stored in a state, which is used elsewhere in form
      // 2) Updates the hoursType value in form data
      onSelectedHoursTypeConfigChange(selectedTypeConfig)
        .then((newConfig) => {
          onHoursTypeChange(newConfig?.fBillableHoursType);
        })
        .catch((err) => {
          // TODO: Figure out how error should be displayed
          console.error(err);
        });
    },
    [onSelectedHoursTypeConfigChange, onHoursTypeChange, options],
  );

  const resolvedSelectProps = {
    ...selectProps,
    ...linkedComponentProps.selectProps,
    onChange: handleSelect,
    value: selectedHoursTypeConfig?.fEmplSubUId || '',
  };

  const getHoursTypeTranslation = useCallback(
    (type: string) => {
      const foundEnum = typeEnums.find((e) => e.code === type);

      return !!foundEnum ? foundEnum.name : type;
    },
    [typeEnums],
  );

  return (
    <FormControl {...FormControlProps}>
      <InputLabel {...linkedComponentProps.inputLabelProps} />
      <Select {...resolvedSelectProps}>
        {!!options &&
          options.map((option) => (
            <MenuItem value={option.fEmplSubUId} key={option.fEmplSubUId}>
              <Box>
                <Typography component={'span'} sx={{ display: 'block' }}>
                  {getHoursTypeTranslation(option.fBillableHoursType)}
                </Typography>
                {!!option.fBillableHoursAmount && (
                  <Typography
                    variant={'caption'}
                    color={theme.palette.text.secondary}
                  >
                    {`${option.fBillableHoursAmount} ${currency}`}
                  </Typography>
                )}
              </Box>
            </MenuItem>
          ))}
      </Select>
      {helperText && (
        <FormHelperText error={resolvedSelectProps.error}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};
